import { type ReactNode } from 'react'
import { Button } from '@/components/Button'
import { Dropdown, DropdownItem, DropdownTitle } from '@/components/Dropdown'
import { IconButton } from '@/components/IconButton'
import type { UserDataType } from '@/features/iam/users/UserData'
import { dateFormatters } from '@/utils/date-util'
import { pluralize } from '@/utils/pluralization-util'
import styles from './UserDetailsInformation.module.css'
import { getUserDataTypeDisplayName } from '@/features/user-management/user-data-type-display'
import { formatCapacity } from '@/features/user-management/capacity-display'
import { isSupported, copy } from 'clipbrd'

export interface UserDetailsInformationProps {
  displayName: string
  email: string
  phone: string
  publicId: string
  activeJobs: number
  totalJobs: number
  lastSeen: Date | null
  dateModified: Date
  dateCreated: Date
  legacyUserId: string
  paymentMethodLast4s: string[]
  userDataType: UserDataType
  payoutMethodStatus: 'Connected' | 'NotConnected' | 'Unknown'
  payoutStripeAccountId: string | null
  canEditUserInformation: boolean
  onEditUserInformation: () => void
  isPro: boolean
  isPlatformAdmin: boolean
  onEditProDetails: () => void
  globalJobCapacity: number
  stateRegion: string
  ptinStatus: string
  yearsExperience: number
  softwareOwnedCount: number
  softwareExperienceCount: number
  specialtiesCount: number
  onEditGlobalCapacity: () => void
  onViewSoftwareOwned: () => void
  onViewSoftwareExperience: () => void
  onViewSpecialties: () => void
}

export function UserDetailsInformation({
  displayName,
  email,
  phone,
  publicId,
  activeJobs,
  totalJobs,
  lastSeen,
  dateModified,
  dateCreated,
  legacyUserId,
  paymentMethodLast4s,
  userDataType,
  payoutMethodStatus,
  payoutStripeAccountId,
  canEditUserInformation,
  onEditUserInformation,
  isPro,
  isPlatformAdmin,
  onEditProDetails,
  globalJobCapacity,
  stateRegion,
  ptinStatus,
  yearsExperience,
  softwareOwnedCount,
  softwareExperienceCount,
  specialtiesCount,
  onEditGlobalCapacity,
  onViewSoftwareOwned,
  onViewSoftwareExperience,
  onViewSpecialties,
}: UserDetailsInformationProps) {
  const copyTextToClipboard = (text: string) => {
    if (isSupported()) {
      copy(text)
    }
  }

  const createCopyMethod = (text: string) => () => copyTextToClipboard(text)

  return (
    <>
      <UserInfoCard
        title="User information"
        actions={
          canEditUserInformation && (
            <DropdownItem
              onPress={onEditUserInformation}
              buttonTestId="user-details-info-actions-edit-user-information"
            >
              Edit user information
            </DropdownItem>
          )
        }
      >
        <UserInfoLine title="Full name" value={displayName} />
        <UserInfoLine
          title="Email address"
          value={email}
          buttonText="Copy"
          buttonAction={() => copyTextToClipboard(email)}
        />
        <UserInfoLine
          title="Phone"
          value={phone}
          buttonText="Copy"
          buttonAction={() => copyTextToClipboard(phone)}
        />
        <UserInfoLine
          title="ID"
          value={publicId}
          buttonText="Copy"
          buttonAction={() => copyTextToClipboard(publicId)}
        />
        <UserInfoLine title="Active jobs" value={activeJobs} />
        <UserInfoLine title="Total jobs" value={totalJobs} />
        <UserInfoLine
          title="Last login"
          value={
            lastSeen ? dateFormatters.dateLongWithTime.format(lastSeen) : '—'
          }
        />
        <UserInfoLine
          title="Last user update"
          value={dateFormatters.dateLongWithTime.format(dateModified)}
        />
        <UserInfoLine
          title="Date joined"
          value={dateFormatters.dateLongWithTime.format(dateCreated)}
        />
        <UserInfoLine title="Legacy ID" value={legacyUserId} />
        <UserInfoLine
          title={`Payment ${pluralize('method', paymentMethodLast4s.length)}`}
          value={paymentMethodLast4s.join(', ') || 'None'}
        />
        {userDataType === 'Pro' &&
          (payoutMethodStatus === 'Connected' ? (
            <UserInfoLine
              title={
                payoutStripeAccountId ? 'Payout method' : 'Payout method status'
              }
              value={payoutStripeAccountId || 'Connected'}
              {...(payoutStripeAccountId && {
                buttonText: 'Copy',
                buttonAction: createCopyMethod(payoutStripeAccountId),
              })}
            />
          ) : payoutMethodStatus === 'NotConnected' ? (
            <UserInfoLine title="Payout method" value="None" />
          ) : (
            <UserInfoLine title="Payout method" value="Unknown" />
          ))}
        <UserInfoLine
          title="Classification"
          value={getUserDataTypeDisplayName(userDataType)}
        />
      </UserInfoCard>

      {isPro && (
        <UserInfoCard
          title="Pro information"
          actions={
            isPlatformAdmin && (
              <DropdownItem
                onPress={onEditProDetails}
                buttonTestId="user-details-pro-information-actions-edit"
              >
                Edit Pro details
              </DropdownItem>
            )
          }
        >
          <UserInfoLine
            title="Global capacity"
            value={`${activeJobs} / ${formatCapacity(globalJobCapacity)}`}
            buttonText={isPlatformAdmin ? 'Edit' : ''}
            buttonAction={onEditGlobalCapacity}
            testId="user-details-pro-information-manage-global-capacity"
          />
          <UserInfoLine title="Location" value={stateRegion} />
          <UserInfoLine title="PTIN" value={ptinStatus} />
          <UserInfoLine title="Years of experience" value={yearsExperience} />
          <UserInfoLine
            title="Software owned"
            value={softwareOwnedCount}
            buttonText="View"
            buttonAction={onViewSoftwareOwned}
            testId="user-details-pro-information-software-owned-view-button"
          />
          <UserInfoLine
            title="Software experience"
            value={softwareExperienceCount}
            buttonText="View"
            buttonAction={onViewSoftwareExperience}
            testId="user-details-pro-information-software-experience-view-button"
          />
          <UserInfoLine
            title="Specialties"
            value={specialtiesCount}
            buttonText="View"
            buttonAction={onViewSpecialties}
            testId="user-details-pro-information-specialties-view-button"
          />
        </UserInfoCard>
      )}
    </>
  )
}

interface UserDetailsCardProps {
  title: string
  actions?: ReactNode
  children: ReactNode
}

function UserInfoCard({ title, actions, children }: UserDetailsCardProps) {
  return (
    <div className={styles.userInfoCard}>
      <div className={styles.userInfoTitle}>
        <span>{title}</span>
        {actions && (
          <Dropdown
            autoDismiss
            position="bottom-end"
            button={
              <IconButton
                name="more"
                label="User actions"
                testId={'user-details-info-actions'}
                color="var(--grey600)"
              />
            }
          >
            <DropdownTitle>
              <span>Actions</span>
            </DropdownTitle>
            {actions}
          </Dropdown>
        )}
      </div>
      {children}
    </div>
  )
}

interface UserInfoLineProps {
  title: string
  value: ReactNode
  buttonText?: string
  buttonAction?: () => void
  testId?: string
}

function UserInfoLine({
  title,
  value,
  buttonText,
  buttonAction,
  testId,
}: UserInfoLineProps) {
  const button = buttonText && buttonAction
  const normalizeTitle = title.toLowerCase().replace(/\s+/g, '-')

  return (
    <div className={styles.userInfoLine}>
      <span className={styles.infoLineTitle}>{title}</span>
      <div className={styles.infoLineSubtitle}>
        <span
          className={styles.infoLineValue}
          data-test={`user-info-line-${normalizeTitle}-value`}
        >
          {value}
        </span>
        {button && (
          <Button
            label={buttonText}
            className={styles.infoLineButton}
            variant="text"
            onPress={buttonAction}
            testId={testId}
          />
        )}
      </div>
    </div>
  )
}
